import { keyBy, sortBy } from 'lodash';
import { ConfigUtils } from '../../config';
import { CampaignAccessors, CampaignUtils } from '../Campaign';
import { DefinitionHacks } from '../Definition';
import { FeatureFlagEnum, FeatureFlagInfoUtils } from '../FeatureFlagInfo';
import { ItemAccessors, ItemDerivers, ItemValidators } from '../Item';
import { getDefinitionKey, getName, isShared } from './accessors';
import { ContainerTypeEnum } from './constants';
function generateInitialCoins() {
    return {
        cp: 0,
        sp: 0,
        ep: 0,
        gp: 0,
        pp: 0,
    };
}
/**
 *
 * @param items
 * @param customItems
 * @param characterId
 * @param partyInfo
 * @param partyInventory
 * @param featureFlagInfo
 * @param characterCoin
 */
export function generateContainers(items, customItems, characterId, partyInfo, partyInventory, featureFlagInfo, characterCoin, preferences) {
    let sharingStateActive = false;
    if (partyInfo && CampaignUtils.isSharingStateActive(CampaignAccessors.getSharingState(partyInfo))) {
        sharingStateActive = true;
    }
    const partyInventoryActive = FeatureFlagInfoUtils.getFeatureFlagInfoValue(FeatureFlagEnum.RELEASE_GATE_IMS, featureFlagInfo) &&
        sharingStateActive;
    const rulesEngineConfig = ConfigUtils.getCurrentRulesEngineConfig();
    // Do we want to display Cointainers? You betcha!
    const canCointainer = FeatureFlagInfoUtils.getFeatureFlagInfoValue(FeatureFlagEnum.RELEASE_GATE_COINTAINERS, featureFlagInfo) &&
        (preferences === null || preferences === void 0 ? void 0 : preferences.enableContainerCurrency) &&
        (rulesEngineConfig === null || rulesEngineConfig === void 0 ? void 0 : rulesEngineConfig.canUseCurrencyContainers);
    //if includeCustomItems on the config is true - customItems are added on the Inventory as Items
    //if includeCustomItems on the config is false/undefined (mobile until v5.1 support) - use the customItem contracts on the character JSON
    const { includeCustomItems } = ConfigUtils.getCurrentRulesEngineConfig();
    const includeCustomItemContractsInEquipment = !includeCustomItems;
    const equipmentContainer = generateContainer(characterId, ContainerTypeEnum.CHARACTER, 'Equipment', 
    //TODO v5.1: All customItems mappings belong to character container for mobile until they can support customItems in containers
    includeCustomItemContractsInEquipment ? [...customItems, ...items] : items, false, null, characterCoin, characterId);
    const itemContainers = items
        .filter(ItemAccessors.isContainer)
        .map((containerItem) => {
        var _a;
        return generateContainer(ItemAccessors.getMappingId(containerItem), ContainerTypeEnum.ITEM, ItemAccessors.getName(containerItem), items, false, containerItem, canCointainer ? (_a = ItemAccessors.getCurrency(containerItem)) !== null && _a !== void 0 ? _a : generateInitialCoins() : null, characterId);
    });
    const campaignId = partyInfo ? CampaignAccessors.getId(partyInfo) : null;
    let partyEquipmentContainer = null;
    let partyContainers = [];
    if (partyInventoryActive && campaignId) {
        partyEquipmentContainer = generateContainer(campaignId, ContainerTypeEnum.CAMPAIGN, 'Party Equipment', partyInventory, true, null, partyInfo ? CampaignAccessors.getCoin(partyInfo) : null, characterId);
        partyContainers = partyInventory
            .filter(ItemAccessors.isContainer)
            .map((containerItem) => {
            var _a;
            return generateContainer(ItemAccessors.getMappingId(containerItem), ContainerTypeEnum.ITEM, ItemAccessors.getName(containerItem), partyInventory, true, containerItem, canCointainer ? (_a = ItemAccessors.getCurrency(containerItem)) !== null && _a !== void 0 ? _a : generateInitialCoins() : null, characterId);
        });
    }
    const playerContainers = sortBy(itemContainers, (container) => getName(container));
    return partyInventoryActive && partyEquipmentContainer
        ? [equipmentContainer, ...playerContainers, partyEquipmentContainer, ...partyContainers]
        : [equipmentContainer, ...playerContainers];
}
/**
 *
 * @param mappingId
 * @param containerType
 * @param name
 * @param availableInventory
 * @param isShared
 * @param item
 * @param coin
 * @param characterId
 */
export function generateContainer(mappingId, containerType, name, availableInventory, isShared, item, coin, characterId) {
    let definitionKey = DefinitionHacks.hack__generateDefinitionKey(containerType, mappingId);
    const contents = availableInventory.filter((item) => ItemAccessors.getContainerDefinitionKey(item) === definitionKey && !ItemAccessors.isContainer(item));
    const infusedItems = contents.filter(ItemAccessors.getInfusion);
    return {
        containerType,
        coin,
        definitionKey,
        mappingId,
        name,
        hasInfusions: !!infusedItems.length,
        item,
        infusedItemMappingIds: infusedItems.map(ItemAccessors.getMappingId),
        itemMappingIds: contents.map(ItemAccessors.getMappingId),
        isEquipped: item ? ItemAccessors.isEquipped(item) : null,
        isShared,
        weightInfo: generateContainerWeightInfo(containerType, contents, item, characterId),
    };
}
export function generateContainerWeightInfo(type, containerInventory, item, characterId) {
    const contentsTotal = ItemDerivers.deriveItemsWeightTotal(containerInventory);
    let capacity = 0;
    let total = contentsTotal;
    let applied = contentsTotal;
    if (type === ContainerTypeEnum.ITEM && item) {
        capacity = ItemAccessors.getCapacityWeight(item);
        applied = ItemAccessors.getWeight(item) + contentsTotal * ItemAccessors.getWeightMultiplier(item);
    }
    else if (type === ContainerTypeEnum.CAMPAIGN) {
        applied = containerInventory.reduce((acc, containerItem) => {
            const itemEquippedToCharacter = ItemValidators.isEquippedToCurrentCharacter(containerItem, characterId);
            if (itemEquippedToCharacter) {
                return acc + ItemAccessors.getWeight(containerItem);
            }
            return acc;
        }, 0);
    }
    return {
        applied,
        capacity,
        total,
    };
}
/**
 *
 * @param containers
 */
export function generateCharacterContainers(containers) {
    return containers.filter((container) => !isShared(container));
}
/**
 *
 * @param containers
 */
export function generatePartyContainers(containers) {
    return containers.filter(isShared);
}
/**
 *
 * @param containers
 */
export function generateContainerLookup(containers) {
    return keyBy(containers, (container) => getDefinitionKey(container));
}
